import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [member, setMember] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);

  // Initialize auth state from localStorage
  useEffect(() => {
    let isInitializing = false; // Prevent multiple simultaneous initializations
    
    const initializeAuth = async () => {
      if (isInitializing) {
        console.log('Auth initialization already in progress, skipping');
        return;
      }
      
      isInitializing = true;
      
      try {
        const storedToken = localStorage.getItem('token');
        const storedUser = localStorage.getItem('user');
        const storedMember = localStorage.getItem('member');
        const storedWallet = localStorage.getItem('wallet');

        console.log('Initializing auth with stored data:', {
          hasToken: !!storedToken,
          hasUser: !!storedUser
        });

        if (storedToken && storedUser) {
          // First, restore the state from localStorage
          try {
            setToken(storedToken);
            setUser(JSON.parse(storedUser));
            if (storedMember) {
              setMember(JSON.parse(storedMember));
            }
            if (storedWallet) {
              setWallet(JSON.parse(storedWallet));
            }
            
            console.log('Auth initialization: State restored from localStorage');
            
            // Then verify token is still valid
            const isValid = await verifyTokenWithToken(storedToken, true);
            
            if (isValid) {
              console.log('Auth initialization: Token verified successfully');
            } else {
              console.log('Auth initialization: Stored token was invalid, cleared');
            }
          } catch (parseError) {
            console.error('Auth initialization: Error parsing stored data:', parseError);
            // Clear invalid data
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('member');
            localStorage.removeItem('wallet');
          }
        } else {
          console.log('Auth initialization: No stored auth data found');
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        logout();
      } finally {
        setLoading(false);
        isInitializing = false;
      }
    };

    initializeAuth();

    // Listen for auth changes from other components
    const handleAuthChange = () => {
      console.log('Auth change event received');
      initializeAuth();
    };

    window.addEventListener('auth-change', handleAuthChange);
    return () => window.removeEventListener('auth-change', handleAuthChange);
  }, []);

  // Verify token with a specific token value
  const verifyTokenWithToken = async (tokenToVerify, duringInitialization = false) => {
    if (!tokenToVerify) {
      console.log('verifyTokenWithToken: No token provided');
      return false;
    }

    console.log('verifyTokenWithToken: Verifying token:', tokenToVerify.substring(0, 20) + '...');

    try {
      const response = await fetch('/api/auth/me', {
        headers: {
          'Authorization': `Bearer ${tokenToVerify}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('verifyTokenWithToken: Response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        console.log('verifyTokenWithToken: Response error:', errorData);
        throw new Error(`Token verification failed: ${errorData.error || 'Unknown error'}`);
      }

      const data = await response.json();
      console.log('verifyTokenWithToken: Verification successful');
      
      // Only update state and localStorage if this isn't during initialization
      // During initialization, state is already set from localStorage
      if (!duringInitialization) {
        console.log('verifyTokenWithToken: Updating auth state and localStorage');
        setToken(tokenToVerify);
        setUser(data.user);
        setMember(data.member);
        setWallet(data.wallet);
        
        // Update localStorage
        localStorage.setItem('token', tokenToVerify);
        localStorage.setItem('user', JSON.stringify(data.user));
        if (data.member) {
          localStorage.setItem('member', JSON.stringify(data.member));
        }
        if (data.wallet) {
          localStorage.setItem('wallet', JSON.stringify(data.wallet));
        }
      } else {
        console.log('verifyTokenWithToken: During initialization, state already restored from localStorage');
      }

      console.log('verifyTokenWithToken: Auth state and localStorage updated successfully');
      return true;
    } catch (error) {
      console.error('verifyTokenWithToken: Verification failed:', error.message);
      if (duringInitialization) {
        console.log('verifyTokenWithToken: During initialization, clearing invalid token without full logout');
        // Clear invalid token data without triggering full logout
        setToken(null);
        setUser(null);
        setMember(null);
        setWallet(null);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('member');
        localStorage.removeItem('wallet');
      } else {
        console.log('verifyTokenWithToken: During normal operation, calling logout');
        logout();
      }
      return false;
    }
  };

  // Verify token and refresh user data
  const verifyToken = async () => {
    return await verifyTokenWithToken(token);
  };

  // Login function
  const login = async (email, password) => {
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      // Store auth data
      const authToken = data.token;
      setToken(authToken);
      setUser(data.user);
      setMember(data.member);
      setWallet(data.wallet);

      // Store in localStorage
      localStorage.setItem('token', authToken);
      localStorage.setItem('user', JSON.stringify(data.user));
      if (data.member) {
        localStorage.setItem('member', JSON.stringify(data.member));
      }
      if (data.wallet) {
        localStorage.setItem('wallet', JSON.stringify(data.wallet));
      }

      return { success: true, user: data.user, member: data.member };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.message };
    }
  };

  // Logout function
  const logout = async () => {
    console.log('logout: Function called, clearing all auth state');
    console.trace('logout: Call stack');
    
    try {
      // Call logout endpoint if token exists
      if (token) {
        console.log('logout: Calling backend logout endpoint');
        await fetch('/api/auth/logout', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear all auth state
      console.log('logout: Clearing state and localStorage');
      setToken(null);
      setUser(null);
      setMember(null);
      setWallet(null);

      // Clear localStorage
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('member');
      localStorage.removeItem('wallet');

      // Trigger auth change event
      console.log('logout: Triggering auth-change event');
      window.dispatchEvent(new Event('auth-change'));
    }
  };

  // Change password function
  const changePassword = async (currentPassword, newPassword) => {
    if (!token) {
      throw new Error('Not authenticated');
    }

    try {
      const response = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Password change failed');
      }

      return { success: true, message: data.message };
    } catch (error) {
      console.error('Change password error:', error);
      return { success: false, error: error.message };
    }
  };

  // Refresh user data
  const refreshUserData = async () => {
    if (token) {
      await verifyToken();
    }
  };

  // Check if user is tournament director
  const isTournamentDirector = () => {
    return user && user.role === 'tournament-director';
  };

  // Check if user is member
  const isMember = () => {
    return user && user.role === 'member' && member;
  };

  // Check if user is authenticated
  const isAuthenticated = () => {
    return !!(token && user);
  };

  // Get authorization header
  const getAuthHeader = () => {
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  // Make authenticated API request
  const authenticatedFetch = async (url, options = {}) => {
    // Don't make requests if still loading auth state
    if (loading) {
      throw new Error('Authentication still loading. Please wait.');
    }
    
    // Don't make requests if not authenticated
    if (!isAuthenticated()) {
      throw new Error('Not authenticated. Please login.');
    }

    const headers = {
      'Content-Type': 'application/json',
      ...getAuthHeader(),
      ...options.headers,
    };

    const response = await fetch(url, {
      ...options,
      headers,
    });

    // Handle unauthorized responses
    if (response.status === 401) {
      console.log('authenticatedFetch: Received 401, calling logout');
      logout();
      throw new Error('Session expired. Please login again.');
    }

    return response;
  };

  const value = {
    // State
    user,
    member,
    wallet,
    token,
    loading,
    
    // Actions
    login,
    logout,
    changePassword,
    verifyToken,
    refreshUserData,
    
    // Helpers
    isAuthenticated,
    isTournamentDirector,
    isMember,
    getAuthHeader,
    authenticatedFetch,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;